import {
    ArrowUpRightIcon,
    SparklesIcon,
    SquareArrowRightIcon,
    SquareArrowUpIcon,
    SquareArrowUpRightIcon,
    XIcon,
} from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import modalStore from '@client/stores/modalStore';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import { MarkdownTextarea, MarkdownTextareaRef } from '@client/component/editor/MarkdownTextarea';
import { StockSearchResult } from './StockSearchInput';

interface AIRecommendModalContentProps {
    modalId: string;
    excludeTickers: string[];
}

interface RecommendedStock extends StockSearchResult {
    reason: string;
}

class ViewStore {
    isLoading = false;
    recommendations: RecommendedStock[] = [];
    errorMessage = '';
    hasSearched = false;
    inputContent = '';

    constructor() {
        makeAutoObservable(this);
    }

    setInputContent(content: string) {
        this.inputContent = content;
    }

    async recommend(userInput: string, excludeTickers: string[]) {
        const trimmed = userInput.trim();

        // 입력값 검증
        if (trimmed.length < 2) {
            this.errorMessage = '2글자 이상 입력해주세요.';
            return;
        }
        if (trimmed.length > 200) {
            this.errorMessage = '200자 이내로 입력해주세요.';
            return;
        }

        // 최대 개수 제한
        if (this.recommendations.length >= 10) {
            this.errorMessage = '최대 10개까지만 추천받을 수 있어요.';
            return;
        }

        this.errorMessage = '';
        this.isLoading = true;
        this.hasSearched = true;

        try {
            // TODO: API 연동
            // const result = await apiClient.ai.recommendStocks({
            //     input: userInput,
            //     excludeTickers: [...excludeTickers, ...this.recommendations.map(r => r.ticker)]
            // });

            // 임시 목 데이터
            await new Promise((resolve) => setTimeout(resolve, 1500));

            // 사용자 입력에 따라 다른 추천 (목 데이터)
            const mockRecommendations: RecommendedStock[] = [];
            const input = userInput.toLowerCase();

            if (input.includes('반도체') || input.includes('ai')) {
                mockRecommendations.push(
                    {
                        ticker: 'NVDA',
                        name: 'NVIDIA Corporation',
                        nameKo: '엔비디아',
                        reason: 'AI 칩 시장의 선두주자',
                    },
                    {
                        ticker: 'TSM',
                        name: 'Taiwan Semiconductor',
                        nameKo: '대만반도체',
                        reason: '세계 최대 파운드리',
                    },
                    {
                        ticker: 'AMD',
                        name: 'Advanced Micro Devices',
                        nameKo: 'AMD',
                        reason: 'GPU 및 CPU 경쟁력',
                    },
                );
            } else if (input.includes('전기차') || input.includes('자동차')) {
                mockRecommendations.push(
                    {
                        ticker: 'TSLA',
                        name: 'Tesla Inc.',
                        nameKo: '테슬라',
                        reason: '전기차 시장 리더',
                    },
                    {
                        ticker: 'GM',
                        name: 'General Motors',
                        nameKo: '제너럴 모터스',
                        reason: '전통 자동차의 전기차 전환',
                    },
                    {
                        ticker: 'F',
                        name: 'Ford Motor',
                        nameKo: '포드',
                        reason: '전기차 라인업 확대',
                    },
                );
            } else if (input.includes('배당') || input.includes('안정')) {
                mockRecommendations.push(
                    {
                        ticker: 'JNJ',
                        name: 'Johnson & Johnson',
                        nameKo: '존슨앤존슨',
                        reason: '60년 연속 배당 증가',
                    },
                    {
                        ticker: 'PG',
                        name: 'Procter & Gamble',
                        nameKo: '프록터앤갬블',
                        reason: '안정적인 소비재 기업',
                    },
                );
            } else {
                // 기본 추천
                mockRecommendations.push(
                    {
                        ticker: 'AAPL',
                        name: 'Apple Inc.',
                        nameKo: '애플',
                        reason: '혁신적인 기술 기업',
                    },
                    {
                        ticker: 'MSFT',
                        name: 'Microsoft Corporation',
                        nameKo: '마이크로소프트',
                        reason: '클라우드 및 소프트웨어 강자',
                    },
                );
            }

            runInAction(() => {
                // 기존 추천 + 새 추천에서 중복 제거
                const existingTickers = new Set([
                    ...excludeTickers,
                    ...this.recommendations.map((r) => r.ticker),
                ]);
                const uniqueNew = mockRecommendations.filter((r) => !existingTickers.has(r.ticker));
                this.recommendations = [...this.recommendations, ...uniqueNew];
                this.isLoading = false;
            });
        } catch (error) {
            runInAction(() => {
                this.errorMessage = 'AI 추천 중 오류가 발생했습니다. 다시 시도해주세요.';
                this.isLoading = false;
            });
            console.error('AI 추천 오류:', error);
        }
    }

    removeStock(ticker: string) {
        this.recommendations = this.recommendations.filter((r) => r.ticker !== ticker);
    }

    get selectedStocks(): StockSearchResult[] {
        return this.recommendations.map((r) => ({
            ticker: r.ticker,
            name: r.name,
            nameKo: r.nameKo,
        }));
    }

    reset() {
        this.recommendations = [];
    }
}

const AIRecommendModalContent = observer(
    ({ modalId, excludeTickers }: AIRecommendModalContentProps) => {
        const viewStore = useMobxInstance(ViewStore);
        const textareaRef = useRef<MarkdownTextareaRef>(null);

        const handleRecommend = async () => {
            const content = textareaRef.current?.getContent() || '';
            if (content.trim()) {
                await viewStore.recommend(content, excludeTickers);
                // 추천 후 입력창 초기화
                textareaRef.current?.setInitialContent('');
                viewStore.setInputContent('');
            }
        };

        const handleComplete = () => {
            modalStore.setReturnValue(modalId, viewStore.selectedStocks);
        };

        const handleCancel = () => {
            modalStore.setReturnValue(modalId, []);
        };

        return (
            <div className="flex flex-col gap-4">
                {/* 입력 영역 - 항상 표시 */}
                <div>
                    <label className="mb-2 block text-sm font-semibold">
                        💡 어떤 분야가 성장할 것 같으세요?
                    </label>
                    <div
                        onKeyDown={(e) => {
                            // Ctrl+Enter 또는 Cmd+Enter로 추천받기
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                e.preventDefault();
                                handleRecommend();
                            }
                        }}
                    >
                        <MarkdownTextarea
                            ref={textareaRef}
                            minRows={2}
                            maxRows={6}
                            placeholder="자유롭게 생각을 얘기해보세요"
                            disabled={viewStore.isLoading}
                            onChange={(e) => {
                                viewStore.setInputContent(e.content);
                            }}
                        />
                    </div>
                    <button
                        className="btn btn-primary btn-block mt-2"
                        disabled={
                            viewStore.isLoading ||
                            viewStore.recommendations.length >= 10 ||
                            !viewStore.inputContent.trim()
                        }
                        onClick={(e) => {
                            e.preventDefault();
                            handleRecommend();
                        }}
                    >
                        <SparklesIcon className="size-5" />
                        {viewStore.recommendations.length > 0 ? '더 찾아보기' : '종목 찾기'}
                    </button>
                </div>

                {/* 에러 메시지 */}
                {viewStore.errorMessage && (
                    <div className="alert alert-error text-sm">
                        <span>{viewStore.errorMessage}</span>
                    </div>
                )}

                {/* 최대 개수 경고 */}
                {viewStore.recommendations.length >= 10 && (
                    <div className="alert alert-warning text-sm">
                        <span>최대 10개까지만 추천받을 수 있어요.</span>
                    </div>
                )}

                {/* 로딩 */}
                {viewStore.isLoading && (
                    <div className="flex flex-col items-center gap-2 py-4">
                        <span className="loading loading-spinner loading-md"></span>
                        <span className="text-base-content/70">AI가 종목을 분석하고 있어요...</span>
                        <span className="text-base-content/50 text-xs">보통 2-3초 정도 걸려요</span>
                    </div>
                )}

                {/* 추천 결과 */}
                {viewStore.recommendations.length > 0 && !viewStore.isLoading && (
                    <>
                        <div>
                            <span className="font-semibold">✨ 추천 종목</span>
                            <span className="text-base-content/60 ml-2 text-sm">
                                ({viewStore.recommendations.length}개)
                            </span>
                        </div>

                        <div className="flex flex-col gap-2">
                            {viewStore.recommendations.map((stock) => (
                                <div
                                    key={stock.ticker}
                                    className="bg-base-200 flex items-center justify-between rounded-lg px-4 py-3"
                                >
                                    <div className="flex-1">
                                        <div className="font-semibold">
                                            {stock.ticker}{' '}
                                            {stock.nameKo && (
                                                <span className="text-base-content/70">
                                                    {stock.nameKo}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-base-content/60 text-sm">
                                            {stock.reason}
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-ghost btn-sm btn-square"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            viewStore.removeStock(stock.ticker);
                                        }}
                                    >
                                        <XIcon className="size-4" />
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* 하단 버튼 */}
                        <div className="flex gap-2">
                            <button
                                className="btn btn-ghost flex-1"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleCancel();
                                }}
                            >
                                취소
                            </button>
                            <button
                                className="btn btn-primary flex-1"
                                disabled={viewStore.recommendations.length === 0}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleComplete();
                                }}
                            >
                                추가하기 ({viewStore.recommendations.length})
                            </button>
                        </div>
                    </>
                )}

                {/* 빈 결과 안내 */}
                {viewStore.recommendations.length === 0 &&
                    !viewStore.isLoading &&
                    viewStore.hasSearched &&
                    !viewStore.errorMessage && (
                        <div className="py-4 text-center">
                            <div className="text-base-content/60 text-sm">
                                추천할 종목을 찾지 못했어요.
                                <br />
                                다른 키워드로 시도해보세요.
                            </div>
                        </div>
                    )}

                {/* 예시 (추천 전에만 표시) */}
                {viewStore.recommendations.length === 0 &&
                    !viewStore.isLoading &&
                    !viewStore.hasSearched && (
                        <div className="bg-base-200 rounded-lg p-3 text-sm">
                            <div className="text-base-content/80 font-semibold">
                                💬 이렇게 답변해보세요
                            </div>
                            <div className="text-base-content/60 mt-2 flex flex-col gap-1 text-xs">
                                <div
                                    className="link link-hover flex items-center"
                                    onClick={() => {
                                        const content = 'AI와 반도체 기술이 더 성장할 것 같아';
                                        textareaRef.current?.setInitialContent(content);
                                    }}
                                >
                                    • AI와 반도체 기술이 더 성장할 것 같아{' '}
                                    <ArrowUpRightIcon className="size-4 stroke-1" />
                                </div>
                                <div
                                    className="link link-hover flex items-center"
                                    onClick={() => {
                                        const content = '전기차 시장이 커질 것 같아';
                                        textareaRef.current?.setInitialContent(content);
                                    }}
                                >
                                    • 전기차 시장이 커질 것 같아{' '}
                                    <ArrowUpRightIcon className="size-4 stroke-1" />
                                </div>
                                <div
                                    className="link link-hover flex items-center"
                                    onClick={() => {
                                        const content = '안정적으로 배당을 받고 싶어';
                                        textareaRef.current?.setInitialContent(content);
                                    }}
                                >
                                    • 안정적으로 배당을 받고 싶어{' '}
                                    <ArrowUpRightIcon className="size-4 stroke-1" />
                                </div>
                            </div>
                        </div>
                    )}
            </div>
        );
    },
);

export default AIRecommendModalContent;
